import React from "react";
import { fetchGeotabLive, fetchGeotabTraces } from "../../api/geotabApi.js";
import GeotabBadge from "./GeotabBadge.jsx";
import { Hint } from "./GeotabHover.jsx";
import GeotabMap from "./GeotabMap.jsx";
import { deltaTone, driverOf, fmtDelta, fmtDuration, fmtTime, traceSegments, truckLabel } from "./geotabFormat.js";
import { LINK_CLASS as LINK, dispatchUrl, geotabUrl } from "./geotabLinks.js";

const REFRESH_MS = 30000;
// 110 px : « Richard Brouillard » en entier, un nom plus long est tronqué.
const LIST_COLS = "grid grid-cols-[110px_76px_56px_56px_34px_84px] items-center gap-1";
// Arrêt hors plan en cours signalé à partir de 30 min.
const LONG_STOP_MIN = 30;

// « il y a 33 h » : âge de la dernière position Geotab.
function positionAge(at, now = Date.now()) {
  const minutes = Math.max(0, Math.round((now - Date.parse(at)) / 60000));
  if (Number.isNaN(minutes)) return "—";
  return minutes < 60 ? `il y a ${minutes} min` : minutes < 48 * 60 ? `il y a ${Math.round(minutes / 60)} h` : `il y a ${Math.round(minutes / 1440)} j`;
}
const KIND = {
  route: ["En route", "#0f5c57"],
  loading: ["Chargement", "#4f6d96"],
  delivery: ["Livraison", "#8a6a2a"],
  off: ["Hors plan", "#c2410c"],
  stop: ["Arrêt", "#475569"],
  depot: ["Au dépôt", "#475569"],
  done: ["Terminé", "#64748b"],
  noplan: ["Sans plan", "#64748b"],
};
const FILTERS = [
  ["all", "Tous", () => true],
  ["route", "En route", (k) => k === "route"],
  ["site", "Sur un site", (k) => k === "loading" || k === "delivery"],
  ["off", "Hors plan", (k) => k === "off" || k === "stop"],
  ["depot", "Dépôt / terminé", (k) => k === "depot" || k === "done" || k === "noplan"],
];

// Prénom seulement ; initiale du nom quand deux camionneurs partagent le même prénom.
function mapLabels(rows) {
  const parts = rows.map((r) => (r.driver_plan || r.driver_geotab || "").trim().split(/\s+/).filter(Boolean));
  const firstCounts = {};
  parts.forEach((p) => { if (p[0]) firstCounts[p[0]] = (firstCounts[p[0]] || 0) + 1; });
  return Object.fromEntries(rows.map((r, i) => {
    const [first, ...rest] = parts[i];
    if (!first) return [r.truck_id, r.truck_name];
    return [r.truck_id, firstCounts[first] > 1 && rest.length ? `${first} ${rest[rest.length - 1][0]}.` : first];
  }));
}

function StateBadge({ kind, title }) {
  const [label, color] = KIND[kind] || KIND.depot;
  return (
    <Hint text={title}>
      <span className="block h-[17px] overflow-hidden whitespace-nowrap rounded-[3px] px-[3px] text-center text-[10.5px] font-bold leading-[17px] text-white" style={{ background: color }}>
        {label}
      </span>
    </Hint>
  );
}

export default function GeotabLiveView({ initialTruck = null, onTruckChange, tolerance, preferences, onPreferencesChange }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [selected, setSelected] = React.useState(initialTruck);
  const filter = preferences.live_filter;
  const setFilter = (value) => onPreferencesChange({ live_filter: value });
  const [trace, setTrace] = React.useState([]);
  const showAll = preferences.live_show_all;
  const setShowAll = (value) => onPreferencesChange({ live_show_all: value });

  React.useEffect(() => {
    let cancelled = false;
    const load = () => fetchGeotabLive()
      .then((payload) => { if (!cancelled) { setData(payload); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err); });
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  // Camions planifiés d'abord ; les boîtiers sans plan aujourd'hui ensuite, par nom.
  const label = (r) => (r.plan || r.driver_geotab ? driverOf(r) : r.truck_name);
  const rows = [...(data?.trucks || [])].sort((a, b) => (!a.plan - !b.plan) || (!a.state - !b.state) || label(a).localeCompare(label(b), "fr"));
  const row = rows.find((r) => r.truck_id === selected);
  React.useEffect(() => setTrace([]), [selected]);
  React.useEffect(() => {
    let cancelled = false;
    const segments = row ? traceSegments(row, { live: true }) : [];
    if (!segments.length || !data?.date) return undefined;
    fetchGeotabTraces(data.date, segments).then((points) => { if (!cancelled) setTrace(points); }).catch(() => {});
    return () => { cancelled = true; };
    // La trace est relue à chaque rafraîchissement des positions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, data]);

  const counts = Object.fromEntries(FILTERS.map(([key, , test]) => [key, rows.filter((r) => r.state && test(r.state.kind)).length]));
  const visible = rows.filter((r) => !r.state || FILTERS.find(([key]) => key === filter)[2](r.state.kind));
  const toggle = (id) => {
    const next = selected === id ? null : id;
    setSelected(next);
    onTruckChange?.(next);
  };

  const labels = mapLabels(rows);
  const layers = { trucks: [], sites: [], fitTrucks: !row };
  rows.forEach((r) => {
    if (!r.position?.lat || !r.state) return;
    // Un camionneur sélectionné est seul sur la carte, sauf si « Afficher tous les camions » est coché.
    if (row && !showAll && r.truck_id !== selected) return;
    layers.trucks.push({
      id: r.truck_id, lat: r.position.lat, lon: r.position.lon, label: labels[r.truck_id], color: (KIND[r.state.kind] || KIND.depot)[1],
      selected: r.truck_id === selected, title: `${driverOf(r)} · ${r.truck_name} — ${r.state.text}`,
    });
  });
  if (row?.plan) {
    const cmp = row.comparison;
    layers.depot = row.plan.depot;
    layers.sites = row.plan.sites.map((s, i) => {
      const real = cmp?.sites[i];
      return { kind: s.kind, lat: s.lat, lon: s.lon, label: s.kind === "depot" ? "D" : i + 1, title: `${i + 1}. ${s.name} — prévu ${fmtTime(s.arrival)}${real?.real_arrival != null ? ` · réel ${fmtTime(real.real_arrival)}` : ""}` };
    });
    layers.plannedLine = row.plan.geometry?.length ? row.plan.geometry : [row.plan.depot, ...row.plan.sites.map((s) => [s.lat, s.lon]), row.plan.depot];
    layers.realLine = trace;
  }

  return (
    <div className="grid min-w-0 gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-[3px] bg-[#c5524a] px-2 py-0.5 text-[12px] font-bold text-white"><span className="h-1.5 w-1.5 bg-white" />EN DIRECT</span>
        <span className="text-[22px] font-bold tabular-nums text-slate-900">{data ? fmtTime(data.now) : "—"}</span>
        <span className="text-[12px] text-slate-500">Positions Geotab actualisées toutes les 30 s</span>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {FILTERS.map(([key, label]) => (
            <button key={key} type="button" aria-pressed={filter === key} onClick={() => setFilter(key)}
              className={`h-6 rounded-[4px] border px-2 text-[12px] font-semibold ${filter === key ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50"}`}>
              {label} <span className="tabular-nums opacity-80">{counts[key]}</span>
            </button>
          ))}
        </div>
      </div>
      {error && <p role="alert" className="rounded-[4px] border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error.message}</p>}
      {!data && !error && <p role="status" className="text-sm text-slate-500">Chargement des positions…</p>}
      {data && (
        <div className="grid min-h-[620px] grid-cols-[auto_minmax(0,1fr)] gap-2 max-[1100px]:grid-cols-1">
          <div className="min-w-0">
            <div className={`${LIST_COLS} sticky top-0 z-10 border border-transparent bg-slate-50 px-2 pb-1 text-center text-[10px] font-bold leading-tight text-slate-500`}>
              <span className="text-left">Camionneur</span><span>État</span><span>Écart plan</span><span>Sur place</span><span>Sites</span><span>Fin prévue</span>
            </div>
            <div className="overflow-hidden rounded-[4px] border border-slate-200 bg-white">
              {visible.map((r) => {
                const s = r.state;
                const active = r.truck_id === selected;
                return (
                  <React.Fragment key={r.truck_id}>
                  <button type="button" onClick={() => toggle(r.truck_id)} aria-pressed={active}
                    className={`block w-full border-t border-slate-100 px-2 py-1 text-left first:border-t-0 hover:bg-slate-50 ${active ? "bg-slate-100 shadow-[inset_3px_0_0_var(--color-accent)]" : ""}`}>
                    <div className={LIST_COLS}>
                      <div className="min-w-0">
                        <b className="block truncate text-[12.5px] leading-tight text-slate-900">{label(r)}</b>
                        {label(r) !== r.truck_name && <span className="block truncate text-[11px] leading-tight text-slate-500">{truckLabel(r)}</span>}
                      </div>
                      {s && !r.plan ? (
                        <>
                          <StateBadge kind={s.kind} title={s.text} />
                          {/* Sans plan : l'âge de la dernière position, et si le boîtier ne communique plus. */}
                          <span className="col-span-4 flex items-center gap-1.5 truncate text-[11.5px] text-slate-600">
                            {r.position?.at ? positionAge(r.position.at) : "—"}
                            {r.position?.communicating === false && <span className="w-[120px] shrink-0"><GeotabBadge tone="bad" title="Le boîtier Geotab n'envoie plus de position.">ne communique plus</GeotabBadge></span>}
                          </span>
                        </>
                      ) : s ? (
                        <>
                          <StateBadge kind={s.kind} title={s.text} />
                          <GeotabBadge tone={deltaTone(s.ecart, tolerance)} title="Écart au plan du dernier événement (arrivée sur le site, départ du site précédent ou retour)">{fmtDelta(s.ecart)}</GeotabBadge>
                          {s.over != null
                            ? <GeotabBadge tone={s.over > 0 ? deltaTone(s.over, tolerance) : "ok"} title="Temps sur place au-delà du prévu">{s.over > 0 ? `+${fmtDuration(s.over)}` : "OK"}</GeotabBadge>
                            : s.off_minutes != null ? <GeotabBadge tone="off" title="Arrêt hors plan en cours">{fmtDuration(s.off_minutes)}</GeotabBadge> : <GeotabBadge tone="empty" />}
                          <GeotabBadge tone="mute" title="Sites terminés / prévus">{s.sites_done}/{s.sites_total}</GeotabBadge>
                          {s.end_at != null ? (
                            <Hint text={`Retour au dépôt prévu maintenant : ${fmtTime(s.end_at)}, pour ${fmtTime(r.plan.return_at)} au plan.`}>
                              <span className="flex items-center gap-1 text-[11.5px] font-semibold tabular-nums text-slate-800">{fmtTime(s.end_at)}
                                <span className="w-12 shrink-0"><GeotabBadge tone={deltaTone(s.end_delta, tolerance)}>{fmtDelta(s.end_delta)}</GeotabBadge></span></span>
                            </Hint>
                          ) : <GeotabBadge tone="empty" />}
                        </>
                      ) : Array.from({ length: 5 }, (_, i) => <GeotabBadge key={i} tone="empty" />)}
                    </div>
                    {s?.kind === "off" && s.off_minutes >= LONG_STOP_MIN && (
                      <div className="mt-0.5 text-[11.5px] font-bold text-[#c2410c]">Arrêt non prévu depuis {fmtDuration(s.off_minutes)}</div>
                    )}
                    {active && s && (
                      <div className="mt-1 text-[12px] leading-snug text-slate-700">
                        {s.text}{r.position?.speed ? ` · ${Math.round(r.position.speed)} km/h` : ""}
                        {r.position?.communicating === false ? " · boîtier hors ligne" : ""}
                      </div>
                    )}
                  </button>
                  {active && r.plan && (
                    <div className="flex flex-wrap gap-2 bg-slate-100 px-2 pb-1.5 shadow-[inset_3px_0_0_var(--color-accent)]">
                      <a className={LINK} href={dispatchUrl(data.date, "day", r.truck_id)}>Ouvrir ce camion dans le Dispatch</a>
                      <a className={LINK} href={geotabUrl({ date: data.date, truckId: r.truck_id })}>Dispatch vs Geotab</a>
                    </div>
                  )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className="relative min-w-0">
            <GeotabMap layers={layers} fitKey={`${selected || "fleet"}-${showAll}`} onTruckClick={toggle} className="h-full min-h-[620px]" />
            {row && (
              <label className="absolute right-2 top-2 z-[500] inline-flex h-7 cursor-pointer items-center gap-2 rounded-[4px] border border-slate-300 bg-white px-2 text-[12px] font-semibold text-slate-700 shadow-sm">
                <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="accent-[var(--color-accent)]" />
                Afficher tous les camions
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
